import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/**
 * Custom hook for scroll-reveal animations using Framer Motion
 */
export function useScrollReveal(threshold = 0.12) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: threshold });
  return { ref, isInView };
}

/**
 * Reusable scroll-reveal wrapper component
 */
export function ScrollReveal({ children, className = '', delay = 0, direction = 'up' }) {
  const { ref, isInView } = useScrollReveal();

  const variants = {
    hidden: {
      opacity: 0,
      y: direction === 'up' ? 30 : direction === 'down' ? -30 : 0,
      x: direction === 'left' ? 30 : direction === 'right' ? -30 : 0,
    },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
      transition: {
        duration: 0.7,
        delay,
        ease: [0.16, 0.84, 0.44, 1],
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={variants}
    >
      {children}
    </motion.div>
  );
}

/**
 * Stagger children animations
 */
export function StaggerContainer({ children, className = '', staggerDelay = 0.1 }) {
  const { ref, isInView } = useScrollReveal();

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: staggerDelay,
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

export const staggerChild = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 0.84, 0.44, 1] },
  },
};
